import type { ReactNode } from 'react'
import { useStripeModeCheck } from '@parkify/ui/stripe-mode'
import { clientStripeMode } from './stripe'
import { fetchStripeMode } from './api'

// The card form fails silently when this site's pk_* and the Worker's sk_* are
// in different modes (test vs live): Elements never mounts, the Pay button stays
// disabled, and there's no console error. This guard fetches the server's mode
// at mount and renders a clear, parker-facing message in place of <Elements/>
// when they diverge. We fail OPEN on network errors / timeout — availability on
// a real-money page beats catching a rare misconfiguration mid-incident.
//
// Copy is intentionally vendor-neutral (this is a parker-facing surface): the
// technical mode detail goes to the console for operators, never to the screen.

export default function StripeModeGuard({ children }: { children: ReactNode }) {
  // The check itself (timeout, fail-open, operator console detail) is shared
  // with consumer: @parkify/ui/stripe-mode.
  const result = useStripeModeCheck(clientStripeMode, fetchStripeMode)

  if (result.status === 'pending') {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>Preparing secure checkout…</p>
      </div>
    )
  }

  if (result.status === 'mismatch' || result.status === 'unknown') {
    return (
      <div className="card">
        <div className="error">
          Payments are temporarily unavailable while our system finishes setting
          up. Please try again in a few minutes — if it keeps happening, email{' '}
          <a href="mailto:help@parkify.com">help@parkify.com</a> and we’ll help
          you take care of your notice.
        </div>
      </div>
    )
  }

  return <>{children}</>
}
